(function () {
  'use strict';

  const SESSION_KEY  = 'ctc-product-files';
  const MAX_BYTES    = 10 * 1024 * 1024; // 10 MB client-side guard
  const WORKER_URL   = 'https://ctc-quote-worker.josh-78f.workers.dev';

  function fmtBytes(b) {
    if (b < 1024) return b + ' B';
    if (b < 1048576) return (b / 1024).toFixed(1) + ' KB';
    return (b / 1048576).toFixed(1) + ' MB';
  }

  function isImg(type) { return type.startsWith('image/'); }

  function iconSvg(type) {
    const base = 'viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="ctc-icon-svg"';
    if (isImg(type)) return `<svg ${base}><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><path d="m21 15-5-5L5 21"/></svg>`;
    if (type === 'application/pdf') return `<svg ${base}><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><path d="M14 2v6h6"/><path d="M9 13h1c.6 0 1 .4 1 1v1c0 .6-.4 1-1 1H9v-3z"/><path d="M13 13h2M13 16h2"/></svg>`;
    return `<svg ${base}><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><path d="M14 2v6h6"/></svg>`;
  }

  // Real upload to Cloudflare R2 via the worker, with XHR progress events.
  function uploadToR2(file, onProgress) {
    return new Promise((resolve, reject) => {
      const fd = new FormData();
      fd.append('file', file);

      const xhr = new XMLHttpRequest();
      xhr.open('POST', WORKER_URL + '/upload');

      if (onProgress) {
        xhr.upload.onprogress = e => {
          if (e.lengthComputable) onProgress(e.loaded / e.total);
        };
      }

      xhr.onload = () => {
        try {
          const data = JSON.parse(xhr.responseText);
          if (xhr.status >= 200 && xhr.status < 300) {
            resolve(data);
          } else {
            reject(new Error(data.error || `Upload failed (${xhr.status})`));
          }
        } catch {
          reject(new Error(`Upload failed (${xhr.status})`));
        }
      };
      xhr.onerror = () => reject(new Error('Network error during upload'));
      xhr.onabort = () => reject(new Error('Upload cancelled'));

      xhr.send(fd);
    });
  }

  // ─── Product page uploads ─────────────────────────────────────────────────

  function initProductUploads() {
    document.querySelectorAll('[data-ctc-product-upload]').forEach(initZone);
  }

  function initZone(zone) {
    if (zone.dataset.ctcUploadInit === '1') return;
    zone.dataset.ctcUploadInit = '1';

    const input = zone.querySelector('.ctc-file-input');
    const list = zone.querySelector('.ctc-file-list');
    const propInput = zone.querySelector('[data-ctc-files-prop]');
    if (!input || !list) return;

    const files = [];

    function save() {
      sessionStorage.setItem(SESSION_KEY, JSON.stringify(
        files.map(f => ({ name: f.name, size: f.size, type: f.type, dataUrl: f.dataUrl || null, url: f.url || null }))
      ));
      if (propInput) propInput.value = files.map(f => f.name).join(', ');
      const form = zone.closest('form');
      if (form) form.dispatchEvent(new CustomEvent('ctc:attached-files-changed', { bubbles: true }));
    }

    list.addEventListener('click', e => {
      const btn = e.target.closest('.ctc-file-remove');
      if (!btn) return;
      const li = btn.closest('li');
      if (li) li.remove();
      if (btn.dataset.name) {
        const idx = files.findIndex(f => f.name === btn.dataset.name);
        if (idx !== -1) files.splice(idx, 1);
        save();
      }
      list.hidden = list.children.length === 0;
    });

    function addErrorItem(f, msg) {
      list.hidden = false;
      const li = document.createElement('li');
      li.className = 'ctc-file-item ctc-file-item--error';
      li.innerHTML = `
        <span class="ctc-file-icon">${iconSvg(f.type)}</span>
        <div class="ctc-file-details">
          <span class="ctc-file-name" title="${f.name}">${f.name}</span>
          <span class="ctc-file-error-msg">${msg}</span>
        </div>
        <button type="button" class="ctc-file-remove" aria-label="Dismiss">&times;</button>
      `;
      list.appendChild(li);
    }

    function addFiles(raw) {
      Array.from(raw).forEach(f => {
        if (f.size > MAX_BYTES) { addErrorItem(f, `Too large (${fmtBytes(f.size)}) — max 10 MB`); return; }

        const entry = { name: f.name, size: f.size, type: f.type, dataUrl: null, url: null };
        files.push(entry);
        save();

        list.hidden = false;
        const li = document.createElement('li');
        li.className = 'ctc-file-item';
        li.innerHTML = `
          <span class="ctc-file-icon">${iconSvg(f.type)}</span>
          <div class="ctc-file-details">
            <span class="ctc-file-name" title="${f.name}">${f.name}</span>
            <div class="ctc-file-status">
              <div class="ctc-prog-wrap">
                <div class="ctc-prog-bar"><div class="ctc-prog-fill" style="width:0%"></div></div>
                <span class="ctc-prog-pct">0%</span>
              </div>
            </div>
          </div>
        `;
        list.appendChild(li);

        const fill   = li.querySelector('.ctc-prog-fill');
        const pctEl  = li.querySelector('.ctc-prog-pct');
        const status = li.querySelector('.ctc-file-status');

        uploadToR2(f, ratio => {
          const p = Math.round(ratio * 100);
          if (fill)  fill.style.width = p + '%';
          if (pctEl) pctEl.textContent = p + '%';
        }).then(({ url }) => {
          if (url) { entry.url = url; save(); }
          if (status) {
            status.innerHTML = `<span class="ctc-file-size">${fmtBytes(f.size)}</span><span class="ctc-file-success-badge" style="margin-left:6px;">Uploaded</span>`;
          }
          const removeBtn = document.createElement('button');
          removeBtn.type = 'button';
          removeBtn.className = 'ctc-file-remove';
          removeBtn.dataset.name = f.name;
          removeBtn.setAttribute('aria-label', 'Remove');
          removeBtn.textContent = '×';
          li.appendChild(removeBtn);
        }).catch(err => {
          const idx = files.findIndex(x => x.name === f.name);
          if (idx !== -1) files.splice(idx, 1);
          save();
          li.classList.add('ctc-file-item--error');
          if (status) status.innerHTML = `<span class="ctc-file-error-msg">${err.message || 'Upload failed — try again'}</span>`;
          const dismissBtn = document.createElement('button');
          dismissBtn.type = 'button';
          dismissBtn.className = 'ctc-file-remove';
          dismissBtn.setAttribute('aria-label', 'Dismiss');
          dismissBtn.textContent = '×';
          li.appendChild(dismissBtn);
        });

        if (isImg(f.type)) {
          const r = new FileReader();
          r.onload = e => {
            entry.dataUrl = e.target.result;
            save();
            const icon = li.querySelector('.ctc-file-icon');
            if (icon) icon.innerHTML = `<img src="${e.target.result}" class="ctc-file-thumb" alt="">`;
          };
          r.readAsDataURL(f);
        }
      });
    }

    input.addEventListener('change', () => { addFiles(input.files); input.value = ''; });
    zone.addEventListener('dragover', e => { e.preventDefault(); zone.classList.add('ctc-dz--over'); });
    zone.addEventListener('dragleave', e => { if (!zone.contains(e.relatedTarget)) zone.classList.remove('ctc-dz--over'); });
    zone.addEventListener('drop', e => { e.preventDefault(); zone.classList.remove('ctc-dz--over'); addFiles(e.dataTransfer.files); });
  }

  // ─── Quote form uploads ───────────────────────────────────────────────────

  function initQuoteUploads() {
    const zone = document.querySelector('[data-ctc-quote-upload]');
    if (!zone) return;

    const input    = zone.querySelector('.ctc-quote-file-input');
    const progList = document.querySelector('.ctc-quote-file-list');
    const panel    = document.querySelector('.ctc-attached-panel');
    const panelList = panel ? panel.querySelector('.ctc-panel-list') : null;
    const namesInput = document.querySelector('[data-ctc-quote-filenames]');

    const names       = [];        // display names (filenames only)
    const fileObjects = [];        // File objects tracked for deduplication
    const fileUrlMap  = new Map(); // name → gofile.io download URL

    function syncNames() {
      if (!namesInput) return;
      namesInput.value = names.map(n => fileUrlMap.has(n) ? `${n}: ${fileUrlMap.get(n)}` : n).join('\n');
    }

    function updatePanelVisibility() {
      if (panel) panel.hidden = !panelList || panelList.children.length === 0;
    }

    function panelAdd(f, fromProduct) {
      if (!panelList) return;
      const li = document.createElement('li');
      li.className = 'ctc-panel-item';
      li.innerHTML = `
        ${f.dataUrl && isImg(f.type)
          ? `<img src="${f.dataUrl}" class="ctc-panel-thumb" alt="">`
          : `<span class="ctc-panel-icon">${iconSvg(f.type)}</span>`}
        <div class="ctc-panel-meta">
          <span class="ctc-panel-name" title="${f.name}">${f.name}</span>
          <span class="ctc-panel-size">${fmtBytes(f.size)}</span>
          ${fromProduct ? '<span class="ctc-panel-badge">From product page</span>' : ''}
        </div>
      `;
      panelList.appendChild(li);
      if (panel) panel.hidden = false;
    }

    function updateMediaTerms() {
      const wrap = document.querySelector('[data-ctc-media-terms-wrap]');
      const cb   = document.querySelector('[data-ctc-media-terms]');
      const val  = document.querySelector('[data-ctc-media-terms-val]');
      if (!wrap || !cb) return;
      const hasFiles = names.length > 0;
      wrap.hidden = !hasFiles;
      cb.required = hasFiles;
      if (!hasFiles) { cb.checked = false; if (val) val.value = 'N/A'; }
    }

    function removeFile(name) {
      const idx = names.indexOf(name);
      if (idx !== -1) names.splice(idx, 1);
      fileUrlMap.delete(name);
      syncNames();
      try { sessionStorage.setItem('ctc-quote-files', namesInput ? namesInput.value : ''); } catch (e) {}

      const fIdx = fileObjects.findIndex(f => f.name === name);
      if (fIdx !== -1) fileObjects.splice(fIdx, 1);

      if (progList) progList.hidden = progList.children.length === 0;
      updatePanelVisibility();
      updateMediaTerms();
    }

    function removeProductFile(name) {
      removeFile(name);
      try {
        const stored = JSON.parse(sessionStorage.getItem(SESSION_KEY) || '[]');
        sessionStorage.setItem(SESSION_KEY, JSON.stringify(stored.filter(f => f.name !== name)));
      } catch (e) {}
    }

    if (progList) {
      progList.addEventListener('click', e => {
        const btn = e.target.closest('.ctc-qf-remove');
        if (!btn) return;
        const li = btn.closest('li');
        if (li) li.remove();
        if (btn.dataset.fromProduct === 'true') {
          removeProductFile(btn.dataset.name);
        } else {
          removeFile(btn.dataset.name);
        }
      });
    }

    function handleFiles(raw) {
      if (!progList) return;
      progList.hidden = false;

      Array.from(raw).forEach(f => {
        if (names.includes(f.name) || fileObjects.some(ef => ef.name === f.name)) return;

        if (f.size > MAX_BYTES) {
          const li = document.createElement('li');
          li.className = 'ctc-qf-item';
          li.innerHTML = `
            <span class="ctc-qf-icon">${iconSvg(f.type)}</span>
            <div class="ctc-qf-details">
              <span class="ctc-qf-name" title="${f.name}">${f.name}</span>
              <span class="ctc-qf-size" style="color:#c00;">Too large (${fmtBytes(f.size)}) — max 10 MB</span>
            </div>
          `;
          progList.appendChild(li);
          return;
        }

        fileObjects.push(f);

        // Build progress row
        const li = document.createElement('li');
        li.className = 'ctc-qf-item ctc-qf-uploading';
        li.innerHTML = `
          <span class="ctc-qf-icon">${iconSvg(f.type)}</span>
          <div class="ctc-qf-details">
            <span class="ctc-qf-name" title="${f.name}">${f.name}</span>
            <div class="ctc-prog-wrap">
              <div class="ctc-prog-bar"><div class="ctc-prog-fill" style="width:0%"></div></div>
              <span class="ctc-prog-pct">0%</span>
            </div>
          </div>
        `;
        progList.appendChild(li);

        const fill = li.querySelector('.ctc-prog-fill');
        const pct  = li.querySelector('.ctc-prog-pct');

        uploadToR2(f, ratio => {
          const p = Math.round(ratio * 100);
          if (fill) fill.style.width = p + '%';
          if (pct)  pct.textContent  = p + '%';
        }).then(({ url }) => {
          // Success — gofile.io URL will be included in email and draft order note
          if (url) fileUrlMap.set(f.name, url);
          li.classList.remove('ctc-qf-uploading');
          const wrap = li.querySelector('.ctc-prog-wrap');
          if (wrap) {
            const sizeEl = document.createElement('span');
            sizeEl.className = 'ctc-qf-size';
            sizeEl.textContent = fmtBytes(f.size);
            wrap.replaceWith(sizeEl);
          }

          names.push(f.name);
          syncNames();

          const addToPanel = dataUrl => {
            panelAdd({ name: f.name, size: f.size, type: f.type, dataUrl }, false);
          };
          if (isImg(f.type)) {
            const r = new FileReader();
            r.onload = e => addToPanel(e.target.result);
            r.readAsDataURL(f);
          } else {
            addToPanel(null);
          }
          updateMediaTerms();

          const removeBtn = document.createElement('button');
          removeBtn.type = 'button';
          removeBtn.className = 'ctc-qf-remove';
          removeBtn.dataset.name = f.name;
          removeBtn.setAttribute('aria-label', 'Remove file');
          removeBtn.textContent = '×';
          li.appendChild(removeBtn);

        }).catch(err => {
          // Upload failed — remove from tracking, show error in row
          const fIdx = fileObjects.findIndex(ef => ef.name === f.name);
          if (fIdx !== -1) fileObjects.splice(fIdx, 1);

          li.classList.remove('ctc-qf-uploading');
          const wrap = li.querySelector('.ctc-prog-wrap');
          if (wrap) {
            const errEl = document.createElement('span');
            errEl.className = 'ctc-qf-size';
            errEl.style.color = '#c00';
            errEl.textContent = err.message || 'Upload failed — try again';
            wrap.replaceWith(errEl);
          }

          const dismissBtn = document.createElement('button');
          dismissBtn.type = 'button';
          dismissBtn.className = 'ctc-qf-remove';
          dismissBtn.setAttribute('aria-label', 'Dismiss');
          dismissBtn.textContent = '×';
          li.appendChild(dismissBtn);
        });
      });
    }

    if (input) input.addEventListener('change', () => handleFiles(input.files));

    zone.addEventListener('dragover', e => { e.preventDefault(); zone.classList.add('ctc-dz--over'); });
    zone.addEventListener('dragleave', e => { if (!zone.contains(e.relatedTarget)) zone.classList.remove('ctc-dz--over'); });
    zone.addEventListener('drop', e => { e.preventDefault(); zone.classList.remove('ctc-dz--over'); handleFiles(e.dataTransfer.files); });

    // Pre-populate from product page sessionStorage (no File objects, names only)
    try {
      const stored = JSON.parse(sessionStorage.getItem(SESSION_KEY) || '[]');
      stored.forEach(f => {
        names.push(f.name);
        if (f.url) fileUrlMap.set(f.name, f.url);
        panelAdd(f, true);
        updateMediaTerms();
        if (progList) {
          progList.hidden = false;
          const li = document.createElement('li');
          li.className = 'ctc-qf-item';
          li.innerHTML = `
            ${f.dataUrl && isImg(f.type)
              ? `<img src="${f.dataUrl}" class="ctc-panel-thumb" style="width:32px;height:32px;object-fit:cover;border-radius:3px;flex-shrink:0;" alt="">`
              : `<span class="ctc-qf-icon">${iconSvg(f.type || '')}</span>`}
            <div class="ctc-qf-details">
              <span class="ctc-qf-name" title="${f.name}">${f.name}</span>
              <span class="ctc-qf-size">${fmtBytes(f.size)}</span>
            </div>
            <button type="button" class="ctc-qf-remove" data-name="${f.name}" data-from-product="true" aria-label="Remove file">&times;</button>
          `;
          progList.appendChild(li);
        }
      });
      syncNames();
    } catch (e) {}

    // Restore files uploaded in a previous submission (update quote flow)
    try {
      const saved = sessionStorage.getItem('ctc-quote-files');
      if (saved) {
        saved.split('\n').forEach(line => {
          line = line.trim();
          if (!line) return;
          const colonIdx = line.indexOf(': https://');
          const name = colonIdx > -1 ? line.slice(0, colonIdx) : line;
          const url  = colonIdx > -1 ? line.slice(colonIdx + 2) : '';
          if (!name || names.includes(name)) return;
          names.push(name);
          if (url) fileUrlMap.set(name, url);
          if (progList) {
            progList.hidden = false;
            const li = document.createElement('li');
            li.className = 'ctc-qf-item';
            li.innerHTML = `
              <span class="ctc-qf-icon">${iconSvg('')}</span>
              <div class="ctc-qf-details">
                <span class="ctc-qf-name" title="${name}">${name}</span>
                <span class="ctc-qf-size">Previously uploaded</span>
              </div>
              <button type="button" class="ctc-qf-remove" data-name="${name}" aria-label="Remove file">&times;</button>
            `;
            progList.appendChild(li);
          }
        });
        syncNames();
        updateMediaTerms();
      }
    } catch (e) {}
  }

  // ─── Auto-expanding textareas ─────────────────────────────────────────────

  function initExpand() {
    document.querySelectorAll('[data-ctc-auto-expand]').forEach(bindAutoExpand);
  }

  function bindAutoExpand(el) {
    if (el.dataset.ctcAutoExpandBound === '1') return;
    el.dataset.ctcAutoExpandBound = '1';
    function resize() { el.style.height = 'auto'; el.style.height = el.scrollHeight + 'px'; }
    el.addEventListener('input', resize);
    resize();
  }

  function initExpandInScope(root) {
    root.querySelectorAll('[data-ctc-auto-expand]').forEach(bindAutoExpand);
  }

  window.ctcInitProductExtras = function (scope) {
    if (!scope || !scope.querySelectorAll) return;
    scope.querySelectorAll('[data-ctc-product-upload]').forEach(initZone);
    initExpandInScope(scope);
  };

  // ─── Bootstrap ────────────────────────────────────────────────────────────

  function init() {
    initProductUploads();
    initQuoteUploads();
    initExpand();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
